/**
 * Tiered free gifts — thresholds are configured in shop (base) currency;
 * compared against cart subtotal in the customer's presentment currency via Shopify.currency.rate.
 */
(function () {
  'use strict';

  var TIER_ORDER = ['one', 'two', 'three'];
  var OPTED_OUT_TIERS_KEY = 'tiered-gifts-opted-out-tiers';
  var updateQueue = Promise.resolve();
  var autoAddInProgress = false;
  var lastKnownCart = null;

  function getConfig() {
    var el = document.getElementById('tiered-gifts-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('tiered-gifts: invalid config', e);
      return null;
    }
  }

  function getCurrencyRate() {
    // Theme settings are entered in the shop currency, while /cart.js returns
    // presentment-currency amounts. Shopify provides the matching storefront
    // conversion rate for comparing those two values.
    if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.rate) {
      return parseFloat(Shopify.currency.rate) || 1;
    }
    return 1;
  }

  function convertThreshold(baseCents, rate) {
    rate = rate != null ? rate : getCurrencyRate();
    return Math.round(baseCents * rate);
  }

  function formatMoney(cents, currencySymbol) {
    var config = getConfig();
    var format = config && config.currencyCodeEnabled && config.moneyWithCurrencyFormat
      ? config.moneyWithCurrencyFormat
      : (config && config.moneyFormat) || window.theme.moneyFormat;

    // Reuse the store's symbol/code placement while making whole-dollar
    // threshold messages match the compact cart messaging treatment.
    if (cents % 100 === 0) {
      format = format
        .replace('amount_with_comma_separator', 'amount_no_decimals_with_comma_separator')
        .replace('amount_with_space_separator', 'amount_no_decimals_with_space_separator')
        .replace('amount', 'amount_no_decimals');
    }

    if (typeof Currency !== 'undefined' && Currency.formatMoney) {
      var formattedMoney = Currency.formatMoney(cents, format);
      return currencySymbol && formattedMoney.indexOf(currencySymbol) === -1
        ? currencySymbol + formattedMoney
        : formattedMoney;
    }
    var fallbackMoney = cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
    return currencySymbol ? currencySymbol + fallbackMoney : fallbackMoney;
  }

  function isFreeGiftProperty(value) {
    return value === 'true' || value === true;
  }

  function getLineItemProperties(item) {
    if (!item || !item.properties) return null;
    if (!Array.isArray(item.properties)) return item.properties;

    var normalized = {};
    item.properties.forEach(function (entry) {
      var key = entry.name != null ? entry.name : entry.first;
      var val = entry.value != null ? entry.value : entry.last;
      if (key) normalized[key] = val;
    });
    return normalized;
  }

  /** Only lines explicitly added as tier free gifts — not regular products sharing the same variant. */
  function isTierGiftLineItem(item) {
    var props = getLineItemProperties(item);
    if (!props) return false;
    if (!isFreeGiftProperty(props._freegift)) return false;
    return !!(props['_gift-tier'] || props._giftTier);
  }

  function getSubtotalForThresholds(cart) {
    // When a bundled section is available, Liquid has already applied the
    // theme's exact promotion rules. Reuse that authoritative subtotal rather
    // than deriving a second, potentially different Ajax-cart total.
    if (typeof cart.tieredGiftsSubtotal === 'number') {
      return cart.tieredGiftsSubtotal;
    }

    // Use the cart total after every Shopify discount, including cart-level
    // codes. A tier gift's final line price is excluded so a paid or
    // misconfigured gift cannot qualify itself.
    var subtotal = cart.total_price;
    if (!cart.items) return subtotal;
    cart.items.forEach(function (item) {
      if (isTierGiftLineItem(item)) {
        subtotal -= item.final_line_price != null ? item.final_line_price : item.line_price;
      }
    });
    return Math.max(0, subtotal);
  }

  function isGiftCardOnlyCart(cart) {
    if (!cart.items || !cart.items.length) return true;
    var giftCardOnly = true;
    cart.items.forEach(function (item) {
      if (!item.gift_card) {
        if (!isTierGiftLineItem(item)) {
          giftCardOnly = false;
        }
      }
    });
    return giftCardOnly;
  }

  function getEnabledTiers(config) {
    return config.tiers.filter(function (t) {
      return t.enabled && t.thresholdCents > 0;
    });
  }

  function getTierById(config, id) {
    return config.tiers.find(function (t) {
      return t.id === id;
    });
  }

  function readOptedOutTiers() {
    try {
      return JSON.parse(sessionStorage.getItem(OPTED_OUT_TIERS_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function writeOptedOutTiers(tiers) {
    try {
      sessionStorage.setItem(OPTED_OUT_TIERS_KEY, JSON.stringify(tiers));
    } catch (e) {}
  }

  function isTierOptedOut(tierId) {
    return readOptedOutTiers().indexOf(tierId) !== -1;
  }

  function optOutTier(tierId) {
    var tiers = readOptedOutTiers();
    if (tiers.indexOf(tierId) === -1) {
      tiers.push(tierId);
      writeOptedOutTiers(tiers);
    }
  }

  function clearTierOptOut(tierId) {
    writeOptedOutTiers(readOptedOutTiers().filter(function (id) {
      return id !== tierId;
    }));
  }

  function getGiftTierId(item) {
    if (!isTierGiftLineItem(item)) return null;
    var props = getLineItemProperties(item);
    if (props['_gift-tier']) return props['_gift-tier'];

    var legacy = props._giftTier;
    if (legacy === 'tier1') return 'one';
    if (legacy === 'tier2') return 'two';
    if (legacy === 'tier3') return 'three';
    return null;
  }

  function getTierGiftItems(cart, tierId) {
    if (!cart.items) return [];
    return cart.items.filter(function (item) {
      return getGiftTierId(item) === tierId;
    });
  }

  function syncGiftOptOuts(previousCart, currentCart, config) {
    if (!previousCart || !currentCart) return;

    var rate = getCurrencyRate();
    var subtotal = getSubtotalForThresholds(currentCart);

    TIER_ORDER.forEach(function (tierId) {
      var tierConfig = getTierById(config, tierId);
      if (!tierConfig || !tierConfig.enabled) return;

      var threshold = convertThreshold(tierConfig.thresholdCents, rate);
      var wasInCart = getTierGiftItems(previousCart, tierId).length > 0;
      var isInCart = getTierGiftItems(currentCart, tierId).length > 0;

      // Dropping below the tier should reset the shopper's opt-out for that tier.
      if (subtotal < threshold) {
        clearTierOptOut(tierId);
        return;
      }

      if (wasInCart && !isInCart) {
        optOutTier(tierId);
      }
    });
  }

  function areAllActiveTiersClaimed(config, cart, state) {
    var enabled = getEnabledTiers(config);
    if (!enabled.length) return false;

    return enabled.every(function (tier) {
      var tierState = state.tiers[tier.id];
      if (!tierState || !tierState.met) return false;
      if (!tier.hasAvailableProducts) return true;
      return getTierGiftItems(cart, tier.id).length > 0 || isTierOptedOut(tier.id);
    });
  }

  function postCartUpdates(updates) {
    if (!updates || Object.keys(updates).length === 0) {
      return Promise.resolve(false);
    }

    var updateUrl =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    updateUrl += 'cart/update.js';

    return fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: updates,
        sections: 'mini-cart',
        sections_url: (window.routes && window.routes.cartUrl) || '/cart'
      })
    }).then(function (response) {
      if (!response.ok) throw new Error('Unable to update free gift');
      // Shopify returns the complete cart here. Reuse it so callers do not need
      // to make a second /cart.js request before rendering the drawer.
      return response.json();
    });
  }

  function mergeAddedItems(cart, response) {
    var responseItems = (response && response.items) || [];
    var returnedItemsByKey = {};
    var existingKeys = {};

    responseItems.forEach(function (item) {
      if (item && item.key) returnedItemsByKey[item.key] = item;
    });

    // /cart/add.js returns added lines, which can be an existing cart line
    // with an updated quantity. Replace that line rather than duplicating it.
    var mergedItems = (cart.items || []).map(function (item) {
      if (item && item.key) {
        existingKeys[item.key] = true;
        if (Object.prototype.hasOwnProperty.call(returnedItemsByKey, item.key)) {
          return returnedItemsByKey[item.key];
        }
      }
      return item;
    });

    responseItems.forEach(function (item) {
      if (!item || !item.key || !existingKeys[item.key]) mergedItems.push(item);
    });

    var merged = Object.assign({}, cart);
    merged.items = mergedItems;
    merged.item_count = mergedItems.reduce(function (total, item) {
      return total + (item.quantity || 0);
    }, 0);
    merged.sections = response && response.sections;
    return merged;
  }

  /**
   * Enforces at most one free-gift line per tier, quantity 1, and removes gifts below threshold.
   */
  function enforceTierGiftLimits(cart, config) {
    if (!config.active) return Promise.resolve(false);

    var rate = getCurrencyRate();
    var subtotal = getSubtotalForThresholds(cart);
    var updates = {};

    TIER_ORDER.forEach(function (tierId) {
      var tierConfig = getTierById(config, tierId);
      var tierItems = getTierGiftItems(cart, tierId);
      if (tierItems.length === 0) return;

      var threshold =
        tierConfig && tierConfig.enabled
          ? convertThreshold(tierConfig.thresholdCents, rate)
          : null;
      var belowThreshold = threshold != null && subtotal < threshold;

      tierItems.forEach(function (item, index) {
        if (!isTierGiftLineItem(item) || !item.key) return;

        if (belowThreshold || index > 0) {
          updates[item.key] = 0;
        } else if (item.quantity > 1) {
          updates[item.key] = 1;
        }
      });
    });

    return postCartUpdates(updates);
  }

  /**
   * Mirrors tiered-gifts-progress.liquid visibility logic.
   */
  function computeProgressState(config, subtotal, rate) {
    var enabled = getEnabledTiers(config);
    var state = {
      tiers: {},
      hideAwayMessage: false,
      hideProgressContainer: false,
      activeProgressTierId: null,
      remainingCents: null,
      showRemaining: true
    };

    enabled.forEach(function (tier) {
      var threshold = convertThreshold(tier.thresholdCents, rate);
      var remainingCents = Math.max(0, threshold - subtotal);
      state.tiers[tier.id] = {
        threshold: threshold,
        remainingCents: remainingCents,
        met: remainingCents <= 0,
        // Each tier is measured independently from zero to its own threshold.
        progressPercent: Math.max(0, Math.min(100, (subtotal / threshold) * 100))
      };
    });

    var t1 = state.tiers.one;
    var t2 = state.tiers.two;
    var t3 = state.tiers.three;

    var msgHidden = { one: true, two: true, three: true };

    TIER_ORDER.some(function (tierId) {
      var tierState = state.tiers[tierId];
      var tierConfig = getTierById(config, tierId);
      if (!tierConfig || !tierConfig.enabled || !tierState || tierState.remainingCents <= 0) {
        return false;
      }
      var priorMet = true;
      for (var i = 0; i < TIER_ORDER.indexOf(tierId); i++) {
        var priorId = TIER_ORDER[i];
        var priorConfig = getTierById(config, priorId);
        if (priorConfig && priorConfig.enabled && state.tiers[priorId] && !state.tiers[priorId].met) {
          priorMet = false;
          break;
        }
      }
      if (priorMet) {
        msgHidden[tierId] = false;
        state.activeProgressTierId = tierId;
        state.remainingCents = tierState.remainingCents;
        return true;
      }
      return false;
    });

    // Hide "X away" only once the final enabled tier is actually reached (not when rounded to $0)
    if (t1 && t1.met && config.tiers[0].enabled && !config.tiers[1].enabled) {
      msgHidden.one = false;
      state.hideAwayMessage = true;
    }
    if (t2 && t2.met && config.tiers[1].enabled && !config.tiers[2].enabled) {
      msgHidden.two = false;
      state.hideAwayMessage = true;
    }
    if (t3 && t3.met && config.tiers[2].enabled) {
      msgHidden.three = false;
      state.hideAwayMessage = true;
    }

    state.messageHidden = msgHidden;

    state.contentHidden = {
      one: !(t1 && t1.met),
      two: !(t2 && t2.met),
      three: !(t3 && t3.met)
    };

    state.showRemaining = !(msgHidden.one && msgHidden.two && msgHidden.three);

  return state;
  }

  function updateProgressBar(config, cart) {
    var progress = document.querySelector('[data-tiered-gifts-progress]');
    if (!progress || !config.active) return null;

    var rate = getCurrencyRate();
    var subtotal = getSubtotalForThresholds(cart);
    var state = computeProgressState(config, subtotal, rate);
    var allClaimed = areAllActiveTiersClaimed(config, cart, state);
    var displayTierId = state.activeProgressTierId;
    if (!displayTierId && !allClaimed) {
      // A reached final tier can still be waiting for its gift to be claimed.
      // Keep that final tier filled until the shopper claims or opts out of it.
      TIER_ORDER.forEach(function (tierId) {
        var tierState = state.tiers[tierId];
        if (tierState && tierState.met) displayTierId = tierId;
      });
    }
    var progressOuter = progress.querySelector('.tiered-gifts-progress__outer');
    if (progressOuter) {
      progressOuter.classList.toggle('hide', allClaimed);
    }

    progress.setAttribute('data-tier-one-content-hidden', state.contentHidden.one ? 'true' : 'false');
    progress.setAttribute('data-tier-two-content-hidden', state.contentHidden.two ? 'true' : 'false');
    progress.setAttribute('data-tier-three-content-hidden', state.contentHidden.three ? 'true' : 'false');

    TIER_ORDER.forEach(function (tierId) {
      var tierState = state.tiers[tierId];
      var tierConfig = getTierById(config, tierId);
      if (!tierConfig || !tierConfig.enabled) return;

      var msgEl = progress.querySelector('.tier-' + tierId + '-progress');
      if (msgEl) {
        var hideMsg = allClaimed ? true : state.messageHidden[tierId];
        msgEl.classList.toggle('hidden', hideMsg);
      }

      var inner = progress.querySelector('.tiered-gifts-progress__inner__tier_' + tierId);
      if (inner && tierState) {
        inner.style.width = tierState.progressPercent + '%';
        // Only one fill may be visible. Otherwise a completed lower tier sits
        // underneath the next tier's 0%-100% fill and makes it look full.
        inner.classList.toggle('active', !allClaimed && tierId === displayTierId);
      }

      var marker = progress.querySelector('.tiered-gifts-progress__tier_' + tierId);
      if (marker && tierState) {
        marker.classList.toggle('active', tierState.met);
      }

      var oosEl = progress.querySelector('[data-tier-oos="' + tierId + '"]');
      if (oosEl) {
        // Only when this tier's threshold is met and every gift in the collection is unavailable
        var showOos =
          tierState &&
          tierState.met &&
          !tierConfig.hasAvailableProducts;
        oosEl.classList.toggle('hide', !showOos);
      }
    });

    var remainingEl = progress.querySelector('[data-tiered-gifts-remaining]');
    if (remainingEl) {
      if (
        !allClaimed &&
        state.showRemaining &&
        !state.hideAwayMessage &&
        state.remainingCents > 0
      ) {
        remainingEl.classList.remove('hide');
        remainingEl.textContent = formatMoney(
          state.remainingCents,
          progress.getAttribute('data-currency-symbol')
        ) + ' away';
      } else {
        remainingEl.classList.add('hide');
      }
    }

    progress.classList.toggle('hide', state.hideProgressContainer);

    return state;
  }

  function updateThresholdSuccessMessages(config, cart) {
    var container = document.querySelector('[data-threshold-success-messages]');
    if (!container || !config.active) return;

    var rate = getCurrencyRate();
    var subtotal = getSubtotalForThresholds(cart);

    ['one', 'two', 'three'].forEach(function (tierId) {
      var tierConfig = getTierById(config, tierId);
      var el = container.querySelector('.success-message__gift-tier-' + tierId);
      if (!el || !tierConfig || !tierConfig.enabled) return;

      var threshold = convertThreshold(tierConfig.thresholdCents, rate);
      var met = subtotal >= threshold && tierConfig.hasAvailableProducts;
      el.classList.toggle('hide', !met);

      if (tierId === 'one' && met) {
        var t2 = getTierById(config, 'two');
        var t3 = getTierById(config, 'three');
        if (t2 && t2.enabled && subtotal >= convertThreshold(t2.thresholdCents, rate) && t2.hasAvailableProducts) {
          el.classList.add('hide');
        }
        if (t3 && t3.enabled && subtotal >= convertThreshold(t3.thresholdCents, rate) && t3.hasAvailableProducts) {
          el.classList.add('hide');
        }
      }
      if (tierId === 'two' && met) {
        var t3b = getTierById(config, 'three');
        if (t3b && t3b.enabled && subtotal >= convertThreshold(t3b.thresholdCents, rate) && t3b.hasAvailableProducts) {
          el.classList.add('hide');
        }
      }
    });
  }

  function updatePromotionDrawerContainers(config, progressState) {
    if (!progressState) return;

    TIER_ORDER.forEach(function (tierId) {
      var container = document.querySelector('.promotion-drawer__gift-tier-container--' + tierId);
      if (!container) return;

      var tierConfig = getTierById(config, tierId);
      if (!tierConfig || !tierConfig.enabled) return;

      var tierState = progressState.tiers[tierId];
      var hideDrawer =
        !tierState ||
        !tierState.met ||
        (tierConfig.singleVariantOnly && !isTierOptedOut(tierId)) ||
        tierConfig.productCount === 0;

      container.classList.toggle('hidden', hideDrawer);
    });
  }

  function updateRewardsButton() {
    var button = document.getElementById('promotion-drawer-toggle');
    var progress = document.querySelector('[data-tiered-gifts-progress]');
    if (!button || !progress) return;

    var allTiersLocked =
      progress.getAttribute('data-tier-one-content-hidden') === 'true' &&
      progress.getAttribute('data-tier-two-content-hidden') === 'true' &&
      progress.getAttribute('data-tier-three-content-hidden') === 'true';

    button.classList.toggle('hide', allTiersLocked);
  }

  function updateReclaimButtons(config, cart, progressState) {
    var container = document.querySelector('[data-tiered-gift-reclaim-buttons]');
    if (!container || !progressState) return;

    container.innerHTML = '';

    var hasButtons = false;
    TIER_ORDER.forEach(function (tierId) {
      var tierConfig = getTierById(config, tierId);
      var tierState = progressState.tiers[tierId];
      if (!tierConfig || !tierConfig.enabled || !tierState) return;
      if (!tierState.met || !tierConfig.hasAvailableProducts || !isTierOptedOut(tierId)) return;
      if (getTierGiftItems(cart, tierId).length > 0) return;

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'footer__rewards-button--button js-open-tier';
      button.setAttribute('data-tier', tierId);
      button.setAttribute('aria-label', 'Claim ' + tierId + ' tier free gift');
      button.textContent = 'Claim free gift';
      container.appendChild(button);
      hasButtons = true;
    });

    container.classList.toggle('hide', !hasButtons);
  }

  function syncAutoAddThresholdAttributes(config) {
    var rate = getCurrencyRate();
    document.querySelectorAll('[data-base-threshold-cents]').forEach(function (el) {
      var base = parseInt(el.getAttribute('data-base-threshold-cents'), 10);
      if (isNaN(base)) return;
      el.setAttribute('data-tiervalue', String(convertThreshold(base, rate)));
    });
  }

  function handleAutoAdd(cart, config) {
    if (!config.active || isGiftCardOnlyCart(cart) || autoAddInProgress) {
      return Promise.resolve(false);
    }

    var rate = getCurrencyRate();
    var subtotal = getSubtotalForThresholds(cart);
    var itemsToAdd = [];

    TIER_ORDER.forEach(function (tierId) {
      var tierConfig = getTierById(config, tierId);
      if (!tierConfig || !tierConfig.enabled) return;

      var autoButtonSelector =
        '.promotion-drawer__gift-tier-container--' +
        tierId +
        ' .cart-upsell-product button[data-tier="' +
        tierId +
        '"]';
      var tierButton = document.querySelector(autoButtonSelector);
      var autoBtn = document.querySelector(autoButtonSelector + '[data-auto-add="true"]');

      // A new cart has no promotion drawer yet. The config carries the same
      // first available variant as the hidden button so initial eligibility can
      // still be claimed before the drawer's first section render.
      if (tierButton && !autoBtn) return;

      var threshold = convertThreshold(tierConfig.thresholdCents, rate);
      var inCart = getTierGiftItems(cart, tierId).length > 0;

      var container = document.querySelector('.promotion-drawer__gift-tier-container--' + tierId);
      if (container) {
        if (subtotal >= threshold) {
          container.classList.remove('hide');
        } else {
          container.classList.add('hide');
        }
      }

      if (subtotal >= threshold && !inCart && !isTierOptedOut(tierId)) {
        var variantId = autoBtn
          ? autoBtn.getAttribute('data-id')
          : tierConfig.autoVariantId;
        if (!variantId) return;
        itemsToAdd.push({
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            _freegift: 'true',
            '_gift-tier': tierId,
            _giftTier: tierConfig.giftTier
          }
        });
      }
    });

    if (itemsToAdd.length === 0) return Promise.resolve(false);

    autoAddInProgress = true;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: itemsToAdd,
        sections: 'mini-cart',
        sections_url: (window.routes && window.routes.cartUrl) || '/cart'
      })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to add free gift');
        return response.json();
      })
      .then(function (response) {
        // The add response includes final drawer HTML; merge its free lines
        // into the current cart only for client-side GWP state hydration.
        return mergeAddedItems(cart, response);
      })
      .finally(function () {
        autoAddInProgress = false;
      });
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('Unable to load cart');
      return r.json();
    });
  }

  /**
   * Replacing a selected gift must be one cart transaction sequence. Keeping it
   * in the same queue as reconciliation prevents an auto-add from racing the
   * removal and leaving both the old and new gift in the drawer.
   */
  function replaceTierGift(tierId, variantId) {
    var config = getConfig();
    var tierConfig = config && getTierById(config, tierId);
    if (!config || !config.active || !tierConfig || !tierConfig.enabled) {
      return Promise.reject(new Error('Gift tier is unavailable'));
    }

    var replacement = updateQueue.then(function () {
      return fetchCart().then(function (cart) {
        var updates = {};
        getTierGiftItems(cart, tierId).forEach(function (item) {
          if (item.key) updates[item.key] = 0;
        });

        return postCartUpdates(updates).then(function (updatedCart) {
          var cartAfterRemoval = updatedCart || cart;
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: parseInt(variantId, 10),
                quantity: 1,
                properties: {
                  _freegift: 'true',
                  '_gift-tier': tierId,
                  _giftTier: tierConfig.giftTier
                }
              }],
              sections: 'mini-cart',
              sections_url: (window.routes && window.routes.cartUrl) || '/cart'
            })
          }).then(function (response) {
            if (!response.ok) throw new Error('Unable to add free gift');
            return response.json().then(function (payload) {
              return mergeAddedItems(cartAfterRemoval, payload);
            });
          });
        }).then(function (finalCart) {
          clearTierOptOut(tierId);
          return finalCart;
        });
      });
    }).then(function (cart) {
      lastKnownCart = cart;
      return cart;
    });

    // Keep the shared queue usable after a failed selection while allowing the
    // caller to show the failure for this specific request.
    updateQueue = replacement.catch(function (error) {
      console.error('tiered-gifts: replacement failed', error);
    });
    return replacement;
  }

  function runUpdate(cart) {
    var config = getConfig();
    if (!config || !config.active) return Promise.resolve();

    syncAutoAddThresholdAttributes(config);

    return Promise.resolve(cart || null)
      .then(function (c) {
        return c || fetchCart();
      })
      .then(function (cartData) {
        syncGiftOptOuts(lastKnownCart, cartData, config);

        return enforceTierGiftLimits(cartData, config).then(function (updatedCart) {
          return updatedCart || cartData;
        });
      })
      .then(function (freshCart) {
        if (!freshCart) return;
        lastKnownCart = freshCart;
        var progressState = updateProgressBar(config, freshCart);
        updateThresholdSuccessMessages(config, freshCart);
        updatePromotionDrawerContainers(config, progressState);
        updateReclaimButtons(config, freshCart, progressState);
        updateRewardsButton();
        return handleAutoAdd(freshCart, config).then(function (cartAfterAutoAdd) {
          return cartAfterAutoAdd || freshCart;
        });
      });
  }

  /**
   * Reapply client-only claimed/opt-out state after the cart section replaces
   * its Liquid markup. This deliberately performs no cart request or mutation.
   */
  function render(cart) {
    var config = getConfig();
    if (!config || !config.active || !cart) return;

    syncAutoAddThresholdAttributes(config);
    lastKnownCart = cart;
    var progressState = updateProgressBar(config, cart);
    updateThresholdSuccessMessages(config, cart);
    updatePromotionDrawerContainers(config, progressState);
    updateReclaimButtons(config, cart, progressState);
    updateRewardsButton();
  }

  function update(cart) {
    var nextUpdate = updateQueue
      .then(function () {
        return runUpdate(cart);
      });

    // Keep the shared queue usable after a failure while returning the failure
    // to the caller, so it can retain the last confirmed drawer markup.
    updateQueue = nextUpdate.catch(function (err) {
        console.error('tiered-gifts: update failed', err);
    });
    return nextUpdate;
  }

  function init() {
    var config = getConfig();
    if (!config || !config.active) return;

    syncAutoAddThresholdAttributes(config);
    update();

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('.cart-upsell-product .quickATC[data-tier]');
      if (!trigger || !trigger.getAttribute('data-id')) return;

      // A deliberate claim means the shopper wants this tier again in this session.
      clearTierOptOut(trigger.getAttribute('data-tier'));
    });
  }

  window.TieredGifts = {
    init: init,
    update: update,
    render: render,
    enforceTierGiftLimits: enforceTierGiftLimits,
    isTierGiftLineItem: isTierGiftLineItem,
    getCurrencyRate: getCurrencyRate,
    convertThreshold: convertThreshold,
    formatMoney: formatMoney,
    getSubtotalForThresholds: getSubtotalForThresholds,
    getTierGiftItems: getTierGiftItems,
    replaceTierGift: replaceTierGift,
    clearTierOptOut: clearTierOptOut,
    isTierOptedOut: isTierOptedOut
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
